// Variables that get the dynamic athletics-style time entry
export const TIME_DURATION_VARIABLE_NAMES = new Set(["time", "rest"]);
export function isTimeDurationVariable(name: string): boolean {
  return TIME_DURATION_VARIABLE_NAMES.has(name.toLowerCase());
}

const SECONDS_PER_UNIT: Record<string, number> = { sec: 1, min: 60, hr: 3600 };

/** Converts a stored measurement + unit (e.g. 1.5, "min") into total seconds. */
export function unitToSeconds(
  value: number | null | undefined,
  unit?: string,
): number | null {
  if (value === null || value === undefined || Number.isNaN(value))
    return null;
  const factor = (unit && SECONDS_PER_UNIT[unit]) || 1;
  return value * factor;
}

/** Converts total seconds back into whatever unit is currently set, so storage shape never changes. */
export function secondsToUnitValue(
  totalSeconds: number | null,
  unit?: string,
): number | null {
  if (totalSeconds === null) return null;
  const factor = (unit && SECONDS_PER_UNIT[unit]) || 1;
  return Math.round((totalSeconds / factor) * 100) / 100;
}

/**
 * Parses a free-typed athletics-style time string into total seconds.
 * Shape is read from the number of colons, not fixed fields:
 *   "30.30"    -> 30.3   (raw seconds)
 *   "1:08.90"  -> 68.9   (m:s.ss)
 *   "2:30:32"  -> 9032   (h:m:s)
 * Overflow in any segment self-corrects because the conversion is plain
 * place-value arithmetic, e.g. "1:75" -> 135 (re-renders as "2:15").
 */
export function parseTimeStringToSeconds(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parts = trimmed.split(":").slice(0, 3);
  let total = 0;
  for (const part of parts) {
    const n = parseFloat(part);
    total = total * 60 + (Number.isNaN(n) ? 0 : n);
  }
  return total;
}

/**
 * Renders total seconds back into the shortest unambiguous shape:
 * under a minute -> "SS.ss", under an hour -> "M:SS.ss", else "H:MM:SS.ss".
 */
export function formatSecondsToTimeString(
  totalSeconds: number | null,
): string {
  if (totalSeconds === null || Number.isNaN(totalSeconds)) return "";
  const sign = totalSeconds < 0 ? "-" : "";
  const abs = Math.abs(totalSeconds);

  const formatSeg = (value: number, pad: boolean) => {
    const whole = Math.floor(value);
    const frac = Math.round((value - whole) * 100);
    const base = pad ? String(whole).padStart(2, "0") : String(whole);
    return frac > 0 ? `${base}.${String(frac).padStart(2, "0")}` : base;
  };

  if (abs < 60) {
    return sign + formatSeg(abs, false);
  }
  if (abs < 3600) {
    const m = Math.floor(abs / 60);
    const s = abs - m * 60;
    return `${sign}${m}:${formatSeg(s, true)}`;
  }
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs - h * 3600 - m * 60;
  return `${sign}${h}:${String(m).padStart(2, "0")}:${formatSeg(s, true)}`;
}

/**
 * Read-only display helper: formats a Time/Rest variable's stored value as
 * an athletics-style time string ("1:08.90"), the same shape the dynamic
 * entry field uses. Returns null for anything that isn't Time/Rest so
 * callers can fall back to their normal number+unit display.
 */
export function formatTimeVariableValue(
  variableName: string,
  measurement: number | null | undefined,
  unit?: string,
): string | null {
  if (!isTimeDurationVariable(variableName)) return null;
  if (measurement === null || measurement === undefined) return null;
  const seconds =
    variableName.toLowerCase() === "time"
      ? measurement
      : unitToSeconds(measurement, unit);
  return formatSecondsToTimeString(seconds);
}
